import { Button, Option } from '@fluentui/react-components';
import { Info20Filled } from '@fluentui/react-icons';
import { useCallback, useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ConfirmDialog, ControlledField, TConfirmDialogState } from '@shared/components';
import { ToastContext } from '@shared/contexts';
import { hasFailedEditorOperation, hasPendingEditorOperations } from '../../../helpers/pendingEditorOperations.helper';
import { isTDraft } from '@modules/items';
import DocumentContext from '../../../contexts/DocumentContext.context';
import { useUpdateConfig } from '../../../api';
import { getDocumentConfig } from '../../../helpers/getDocumentConfig.helper';
import { getSectionCount } from '../../../tiptap/nodes/section/SectionNode';
import { countSections, loadDocument, wholeDocumentHtml, withoutSections } from '../../../largeDocument';
import { TDocument, TDraft, TVersion } from '../../../types';
import { EDITOR_OPERATION_FAILED_MESSAGE } from '../../../constants';

const schema = yup.object({
  headingStart: yup
    .number()
    .typeError('Please provide a number')
    .required('Please provide a number.')
    .integer('Please provide an integer.')
    .min(1, 'Please provide a number greater than or equal 1.'),
  uiOrientation: yup
    .array(yup.mixed<string>().oneOf(['Portrait', 'Landscape']).required())
    .length(1, 'Please select an option.'),
});
type TFormData = yup.InferType<typeof schema>;

type TProps = {
  doc: TDocument;
  version: TVersion | TDraft;
  versionId: string; // version ID, 'draft' for draft documents
};

const General = ({ doc, version, versionId }: TProps) => {
  const { show } = useContext(ToastContext);
  const documentContext = useContext(DocumentContext);
  const { isEdit, editor, save } = documentContext || {};
  const isVersion = !isTDraft(version);
  const isView = !isEdit || isVersion;

  const [confirmDialog, setConfirmDialog] = useState<TConfirmDialogState>({
    isOpen: false,
    title: '',
    content: '',
    onConfirm: () => {},
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues: { headingStart: 1, uiOrientation: ['Portrait'] },
  });

  const updateConfig = useUpdateConfig();

  useEffect(() => {
    if (version) {
      const config = getDocumentConfig(version?.config);
      reset(config);
    }
  }, [doc._id, versionId, isEdit]);

  const performConfigUpdate = useCallback(
    (cfg: TFormData, unwrapSections: boolean = false) => {
      const isLandscape = cfg.uiOrientation?.includes('Landscape') ?? false;

      const applyConfig = () => {
        // Sync editor storage (triggers toolbar update via transaction)
        editor?.commands.setLandscapeMode(isLandscape);

        // Update the config (backend mutation)
        updateConfig.mutate({
          id: doc._id,
          config: {
            headingStart: cfg.headingStart,
            landscape: isLandscape,
          },
        });
      };

      // Unwrap sections if requested (switching to landscape with existing sections)
      if (unwrapSections && editor) {
        // `unwrapAllSections` walks the editor, which holds one part of a large document - the rest
        // of the sections would stay in the stored html while the document is flagged landscape. So
        // strip them from the whole document and reload it, the same way the fast loading switch does.
        const whole = wholeDocumentHtml(editor);
        if (whole === null) editor.commands.unwrapAllSections();
        else {
          // A whole-document rebuild unmounts the node views that still own insertion cleanup.
          if (hasPendingEditorOperations(editor, doc._id)) {
            if (hasFailedEditorOperation(editor)) show('error', EDITOR_OPERATION_FAILED_MESSAGE);
            else show('warning', 'Please wait for the inserted content to finish loading before changing orientation.');
            return;
          }
          loadDocument({
            editor,
            doc,
            version,
            html: withoutSections(whole),
            onLoaded: () => {
              applyConfig();
              // The reload arrives as a system transaction during a swap, and the autosave ignores
              // both - so nothing else would ever write the document without its sections.
              save?.({ forceSave: true });
            },
          });
          return;
        }
      }

      applyConfig();
    },
    [doc._id, doc, version, editor, save, updateConfig]
  );

  const onSubmit = useCallback(
    (cfg: TFormData) => {
      const currentConfig = getDocumentConfig(version?.config);
      const isCurrentlyPortrait = !currentConfig.landscape;
      const isSwitchingToLandscape = cfg.uiOrientation?.includes('Landscape') ?? false;

      // Check if switching from portrait to landscape
      if (isCurrentlyPortrait && isSwitchingToLandscape && editor) {
        // The whole document when it is loaded in parts - counting the editor would say 1 for a
        // document with five sections, because it holds one part of it.
        const whole = wholeDocumentHtml(editor);
        const sectionCount = whole !== null ? countSections(whole) : getSectionCount(editor.state);

        // If there are sections, show confirmation dialog
        if (sectionCount > 0) {
          setConfirmDialog({
            isOpen: true,
            title: 'Switch to Landscape',
            content: `This will remove your ${sectionCount} landscape section${sectionCount > 1 ? 's' : ''} since all pages will be landscape. Content will be preserved.`,
            buttonLabel: 'Continue',
            onConfirm: () => {
              performConfigUpdate(cfg, true);
              setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
            },
          });
          return;
        }
      }

      // No sections or not switching to landscape - proceed directly
      performConfigUpdate(cfg, false);
    },
    [doc._id, version?.config, editor, performConfigUpdate]
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col space-y-2">
          <ControlledField
            label="Heading start number"
            name="headingStart"
            inputType="number"
            control={control}
            errors={errors}
            required
            disabled={isView}
          />
          <ControlledField
            label="Document orientation"
            placeholder="Select"
            name="uiOrientation"
            inputType="dropdown"
            getLabel={(v) => v}
            control={control}
            errors={errors}
            fixedHeight
            required
            disabled={isView}
          >
            <Option value="Portrait">Portrait</Option>
            <Option value="Landscape">Landscape</Option>
          </ControlledField>
          {isView && (
            <p className="flex items-center space-x-1">
              <Info20Filled />
              <span>{doc?.draft ? 'Edit' : 'Create'} a draft to change document settings.</span>
            </p>
          )}
          <div>
            <Button type="submit" appearance="primary" disabled={isView}>
              Update
            </Button>
          </div>
        </div>
      </form>
      <ConfirmDialog state={confirmDialog} setState={setConfirmDialog} />
    </>
  );
};

export default General;
